import { useEffect } from 'react';

export interface PageSEOOptions {
    title: string;
    description: string;
    canonicalPath: string;
}

const BASE_URL = 'https://baywhiz.com';

/**
 * Sets document title, meta description, OpenGraph tags, and canonical link tag
 * for public marketing and legal pages, and restores defaults on unmount.
 */
export function usePageSEO({ title, description, canonicalPath }: PageSEOOptions) {
    useEffect(() => {
        const previousTitle = document.title;
        document.title = title;

        const cleanPath = canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`;
        const canonicalUrl = cleanPath === '/' ? `${BASE_URL}/` : `${BASE_URL}${cleanPath}`;

        // Meta description
        let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
        const prevDesc = metaDesc?.getAttribute('content') ?? null;
        if (!metaDesc) {
            metaDesc = document.createElement('meta');
            metaDesc.setAttribute('name', 'description');
            document.head.appendChild(metaDesc);
        }
        metaDesc.setAttribute('content', description);

        // Open Graph Title / Description / URL
        const setMetaProp = (prop: string, value: string, attr: 'property' | 'name' = 'property') => {
            let el = document.querySelector(`meta[${attr}="${prop}"]`) as HTMLMetaElement | null;
            if (!el) {
                el = document.createElement('meta');
                el.setAttribute(attr, prop);
                document.head.appendChild(el);
            }
            el.setAttribute('content', value);
        };

        setMetaProp('og:title', title);
        setMetaProp('og:description', description);
        setMetaProp('og:url', canonicalUrl);
        setMetaProp('twitter:title', title, 'name');
        setMetaProp('twitter:description', description, 'name');

        // Canonical link tag
        let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
        const prevCanonical = canonicalEl?.getAttribute('href') ?? null;
        const createdCanonical = !canonicalEl;
        if (!canonicalEl) {
            canonicalEl = document.createElement('link');
            canonicalEl.setAttribute('rel', 'canonical');
            document.head.appendChild(canonicalEl);
        }
        canonicalEl.setAttribute('href', canonicalUrl);

        return () => {
            document.title = previousTitle;
            if (metaDesc && prevDesc !== null) {
                metaDesc.setAttribute('content', prevDesc);
            }
            if (canonicalEl) {
                if (createdCanonical) {
                    canonicalEl.remove();
                } else if (prevCanonical !== null) {
                    canonicalEl.setAttribute('href', prevCanonical);
                }
            }
        };
    }, [title, description, canonicalPath]);
}
